import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

/** Module the invoice belongs to (mirrors backend WorkflowType). */
export type InvoiceReportModule =
  | "NEW_APP"
  | "FOLLOW_UP"
  | "OPPOSITION"
  | "ASSIGNMENT"
  | "RENEWAL"
  | "LEGAL_CERTIFICATE"
  | "SURVEILLANCE";

/** Sub-stage / invoice type within the module (mirrors backend InvoiceReportSubStage). */
export type InvoiceReportSubStage =
  | "NEW_FILING"
  | "EXPEDITED"
  | "FC_REPLY"
  | "REPLY_TO_OBJECTION"
  | "HEARING"
  | "HEARING_REVISED"
  | "TM_M_POSTPONE"
  | "TM_M_AFTER_HEARING"
  | "SUBSEQUENT_HEARING_2"
  | "SUBSEQUENT_HEARING_3"
  | "SUBSEQUENT_HEARING_4"
  | "COUNTER"
  | "EVIDENCE"
  | "OPPOSITION_HEARING"
  | "TM_M_BEFORE_OPPOSITION_HEARING"
  | "TM_M_AFTER_OPPOSITION_HEARING"
  | "SUBSEQUENT_OPPOSITION_HEARING_2"
  | "SUBSEQUENT_OPPOSITION_HEARING_3"
  | "SUBSEQUENT_OPPOSITION_HEARING_4"
  | "FOLLOW_UP"
  | "OPPOSITION_DISCUSSION"
  | "OPPOSITION_EVIDENCE";

/** Computed status (mirrors backend InvoiceReportStatus). */
export type InvoiceReportStatus =
  | "PAID"
  | "PARTIALLY_PAID"
  | "UNPAID"
  | "OVERDUE";

export type InvoiceReportSortBy =
  | "invoiceDate"
  | "dueDate"
  | "totalAmount"
  | "collectedAmount"
  | "outstandingAmount";

export type InvoiceReportSortOrder = "ASC" | "DESC";

export interface InvoiceReportRow {
  module: InvoiceReportModule;
  subStage: InvoiceReportSubStage;
  invoiceId: string;
  leadId: string;
  leadNo: string | null;
  clientName: string | null;
  markName: string | null;
  invoiceNo: string | null;
  /** Backend formats as DD-MM-YYYY string. */
  invoiceDate: string | null;
  /** Backend formats as DD-MM-YYYY string. */
  dueDate: string | null;
  totalAmount: number;
  collectedAmount: number;
  outstandingAmount: number;
  status: InvoiceReportStatus;
}

export interface InvoiceReportSummary {
  totalInvoiceAmount: number;
  totalCollectedAmount: number;
  totalOutstandingAmount: number;
  invoiceCount: number;
}

export interface InvoiceReportResponse {
  rows: InvoiceReportRow[];
  summary: InvoiceReportSummary;
  page: number;
  limit: number;
  total: number;
}

export interface InvoiceReportQueryParams {
  page?: number;
  limit?: number;
  modules?: InvoiceReportModule[];
  subStages?: InvoiceReportSubStage[];
  statuses?: InvoiceReportStatus[];
  invoiceDateFrom?: string;
  invoiceDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  totalMin?: number;
  totalMax?: number;
  collectedMin?: number;
  collectedMax?: number;
  outstandingMin?: number;
  outstandingMax?: number;
  leadId?: string;
  clientName?: string;
  invoiceNo?: string;
  search?: string;
  includeInactive?: boolean;
  sortBy?: InvoiceReportSortBy;
  sortOrder?: InvoiceReportSortOrder;
}

interface ApiErrorResponse {
  success?: false;
  message?: string | string[];
  error?: string;
  statusCode?: number;
}

function getApiErrorMessage(json: ApiErrorResponse | undefined, fallback: string): string {
  if (!json) return fallback;
  if (Array.isArray(json.message)) {
    return json.message.map(String).filter(Boolean).join(" ");
  }
  if (typeof json.message === "string" && json.message.trim()) {
    return json.message;
  }
  if (typeof json.error === "string" && json.error.trim()) {
    return json.error;
  }
  return fallback;
}

function appendList(params: URLSearchParams, key: string, values: string[] | undefined) {
  if (!values || values.length === 0) return;
  for (const v of values) {
    if (v) params.append(key, v);
  }
}

function appendIfSet(
  params: URLSearchParams,
  key: string,
  value: string | number | boolean | undefined
) {
  if (value === undefined || value === null) return;
  if (typeof value === "string" && !value.trim()) return;
  params.set(key, String(value));
}

/**
 * GET /reports/invoices — unified invoice report across all modules.
 * The backend returns rows + summary totals + total count for pagination.
 */
export async function listInvoiceReport(
  opts: InvoiceReportQueryParams = {}
): Promise<InvoiceReportResponse> {
  const params = new URLSearchParams();
  params.set("page", String(opts.page ?? 1));
  params.set("limit", String(opts.limit ?? 25));

  appendList(params, "modules", opts.modules);
  appendList(params, "subStages", opts.subStages);
  appendList(params, "statuses", opts.statuses);

  appendIfSet(params, "invoiceDateFrom", opts.invoiceDateFrom);
  appendIfSet(params, "invoiceDateTo", opts.invoiceDateTo);
  appendIfSet(params, "dueDateFrom", opts.dueDateFrom);
  appendIfSet(params, "dueDateTo", opts.dueDateTo);

  appendIfSet(params, "totalMin", opts.totalMin);
  appendIfSet(params, "totalMax", opts.totalMax);
  appendIfSet(params, "collectedMin", opts.collectedMin);
  appendIfSet(params, "collectedMax", opts.collectedMax);
  appendIfSet(params, "outstandingMin", opts.outstandingMin);
  appendIfSet(params, "outstandingMax", opts.outstandingMax);

  appendIfSet(params, "leadId", opts.leadId);
  appendIfSet(params, "clientName", opts.clientName);
  appendIfSet(params, "invoiceNo", opts.invoiceNo);
  appendIfSet(params, "search", opts.search?.trim());
  appendIfSet(params, "includeInactive", opts.includeInactive);

  appendIfSet(params, "sortBy", opts.sortBy);
  appendIfSet(params, "sortOrder", opts.sortOrder);

  const res = await apiFetch(`${API_BASE}/reports/invoices?${params}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | InvoiceReportResponse
    | { data: InvoiceReportResponse }
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to load invoice report")
    );
  }

  if (json && typeof json === "object" && "data" in json && (json as { data: unknown }).data) {
    return (json as { data: InvoiceReportResponse }).data;
  }
  return json as InvoiceReportResponse;
}
